import React, { useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useGame } from '../context/GameContext';
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { GlowFilter } from 'pixi-filters';
import {
  GRID_SIZE,
  CURSOR_MODE,
  STATUS_ICONS,
  MODE,
  GAME_GRID,
  GRID_PAINT_CURSOR,
  GRID_ERASE_CURSOR,
} from '../constants/gameConstants';
import { OBJECT_ITEMS, STRUCTURE_ITEMS, PAINT_COLORS, PAINT_MODE } from '../data/paintObjects';
import { getBattlemapImageUrl, getBattlemapById } from '../services/battlemapService';
import { createDragHandler } from '../utils/dragHelpers';
import heartIcon from '../assets/grid/heart-solid.png';
import statusIcon from '../assets/grid/briefcase-medical-solid.png';
import bookIcon from '../assets/grid/book-solid.png';
import trashIcon from '../assets/grid/trash-solid.png';
import removeIcon from '../assets/grid/circle-minus-solid.png';

const GameGrid = forwardRef((_props, ref) => {
  const {
    gridWidth,
    gridHeight,
    gridColors = {},
    gridWalls = {},
    gridObjects = {},
    gridObjectInstances = {},
    coins = [],
    isCreator = false,
    canManageEnemies = false,
    canPaint = false,
    permissions = {},
    onCoinDragEnd,
    onObjectDragEnd,
    zoom = GAME_GRID.VIEWPORT_DEFAULT_ZOOM,
    setZoom,
    selectedCoinId,
    onGridSelectCoin: onSelectCoin,
    selectedObjectId,
    onGridSelectObject: onSelectObject,
    cursorMode,
    onCoinHealthAction,
    onCoinStatusAction,
    onCoinRemoveAction,
    onCoinDeleteAction,
    onCoinStatBlockAction,
    mode,
    paintMode = PAINT_MODE.LINE,
    selectedPaintColor,
    areaPaintStart = null,
    areaPaintEnd = null,
    wallHoverCorner = null,
    wallDragStartCorner = null,
    wallDragEndCorner = null,
    onGridMouseDown,
    onGridMouseEnter,
    onGridMouseUp,
    onGridMouseLeave,
    selectedBattlemapId = GAME_GRID.BATTLEMAP_NONE,
    onGridDeselectAll: onDeselectAll,
  } = useGame();

  const isPaintMode = mode === MODE.PAINT;
  const onZoomChange = setZoom;
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const viewportRef = useRef(null);
  const coinsContainerRef = useRef(null);
  const healthOverlayRef = useRef(null);  // Health bars layer (above all coins)
  const nameOverlayRef = useRef(null);    // Name labels layer (above health bars)
  const coinActionsOverlayRef = useRef(null); // Selected-token action buttons (above names)
  const paintLayerRef = useRef(null);
  const texturesRef = useRef({});
  const isPaintingRef = useRef(false);
  const [isViewportReady, setIsViewportReady] = useState(false);
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  /** Bumps when an async battlemap texture finishes loading so the grid redraws (setTexturesLoaded(prev=>prev) would not). */
  const [battlemapLoadTick, setBattlemapLoadTick] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const zoomUpdateTimeoutRef = useRef(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    screenToWorld: (screenX, screenY) => {
      if (!viewportRef.current || !containerRef.current) return null;
      
      // Get container position to calculate relative screen position
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeX = screenX - containerRect.left;
      const relativeY = screenY - containerRect.top;
      
      // Use viewport's toWorld method to convert to world coordinates
      const worldPos = viewportRef.current.toWorld(relativeX, relativeY);
      return { x: worldPos.x, y: worldPos.y };
    },
    worldToScreen: (worldX, worldY) => {
      if (!viewportRef.current || !containerRef.current) return null;
      
      // Use viewport's toScreen method to convert world coordinates to screen coordinates
      const screenPos = viewportRef.current.toScreen(worldX, worldY);
      
      // Get container position to calculate absolute screen position
      const containerRect = containerRef.current.getBoundingClientRect();
      
      return { 
        x: screenPos.x + containerRect.left, 
        y: screenPos.y + containerRect.top 
      };
    }
  }), []);

  // Check if position is within valid grid bounds
  const isWithinBounds = useCallback((x, y, coinSize = GRID_SIZE) => {
    const gridMaxX = gridWidth * GRID_SIZE;
    const gridMaxY = gridHeight * GRID_SIZE;

    // Check bounds
    if (x < 0 || x + coinSize > gridMaxX) {
      return false;
    }
    if (y < 0 || y + coinSize > gridMaxY) {
      return false;
    }

    return true;
  }, [gridWidth, gridHeight]);

  // Snap position to grid
  const snapToGrid = useCallback((x, y) => {
    const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
    const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
    return { x: snappedX, y: snappedY };
  }, []);

  // Validate coin position
  const validateCoinPosition = useCallback((coinContainer, coin, startPos, snapped, coinSize) => {
    if (isWithinBounds(snapped.x, snapped.y, coinSize)) {
      // Notify parent of position change
      if (onCoinDragEnd) {
        onCoinDragEnd(coin, snapped);
      }

      // Update visual position
      coinContainer.x = snapped.x + coinSize / 2;
      coinContainer.y = snapped.y + coinSize / 2;
    } else {
      // Reset to original position if dropped outside bounds
      coinContainer.x = startPos.x;
      coinContainer.y = startPos.y;
    }
  }, [isWithinBounds, onCoinDragEnd]);

  // Load textures for paint colors
  useEffect(() => {
    const loadTextures = async () => {
      try {
        // Load terrain textures
        for (const item of [...PAINT_COLORS, ...OBJECT_ITEMS, ...STRUCTURE_ITEMS]) {
          if (item.image) {
            try {
              // PixiJS 8: Use Assets.load() for better texture management
              const texture = await PIXI.Assets.load(item.image);
              const textureKey = item.textureKey || item.color;
              texturesRef.current[textureKey] = texture;
            } catch (e) {
              console.warn('Failed to load texture for', item.name, e);
            }
          }
        }
        
        // Mark textures as loaded
        console.log('All textures loaded, setting texturesLoaded to true');
        setTexturesLoaded(true);
      } catch (e) {
        console.error('Error loading textures', e);
        // Still mark as loaded to allow rendering with fallback colors
        setTexturesLoaded(true);
      }
    };
    loadTextures();
  }, []);

  // Lazy load battlemap textures when selected
  useEffect(() => {
    const loadBattlemapTexture = async () => {
      if (!selectedBattlemapId || selectedBattlemapId === GAME_GRID.BATTLEMAP_NONE) return;
      
      const battlemapKey = `battlemap-${selectedBattlemapId}`;
      
      // Check if already loaded
      if (texturesRef.current[battlemapKey]) {
        console.log('[loadBattlemapTexture] Battlemap already loaded:', selectedBattlemapId);
        return;
      }
      
      const imageUrl = getBattlemapImageUrl(selectedBattlemapId);
      if (!imageUrl) {
        console.warn('[loadBattlemapTexture] No image URL for battlemap:', selectedBattlemapId);
        return;
      }
      
      try {
        console.log('[loadBattlemapTexture] Loading battlemap:', selectedBattlemapId, 'from:', imageUrl);
        const texture = await PIXI.Assets.load(imageUrl);
        texturesRef.current[battlemapKey] = texture;
        console.log('[loadBattlemapTexture] Successfully loaded battlemap:', selectedBattlemapId);
        setBattlemapLoadTick((t) => t + 1);
      } catch (e) {
        console.error('[loadBattlemapTexture] Failed to load battlemap:', selectedBattlemapId, e);
      }
    };
    
    loadBattlemapTexture();
  }, [selectedBattlemapId]);

  // Initialize app once - use container size for canvas
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const canvasWidth =
      container.clientWidth || window.innerWidth - GAME_GRID.CANVAS_FALLBACK_WIDTH_OFFSET;
    const canvasHeight =
      container.clientHeight || window.innerHeight - GAME_GRID.CANVAS_FALLBACK_HEIGHT_OFFSET;

    let isMounted = true;

    const initApp = async () => {
      if (appRef.current) {
        await appRef.current.destroy(true);
        appRef.current = null;
        viewportRef.current = null;
      }

      const app = new PIXI.Application();
      
      // PixiJS 8 uses async init
      await app.init({
        width: canvasWidth,
        height: canvasHeight,
        backgroundAlpha: GAME_GRID.PIXI_BACKGROUND_ALPHA,
        antialias: true,
        resolution: (window.devicePixelRatio || 1) * GAME_GRID.PIXI_RESOLUTION_MULTIPLIER,
        autoDensity: true,
      });

      if (!isMounted) return;

      appRef.current = app;

      const canvasWrapper = container.querySelector('.pixi-canvas-wrapper');
      if (canvasWrapper) {
        canvasWrapper.innerHTML = '';
        canvasWrapper.appendChild(app.canvas);
        
        // Ensure canvas can receive all pointer events
        app.canvas.style.pointerEvents = 'auto';
        app.canvas.style.touchAction = 'none';
      }

      // Create viewport for zoom and pan with touch support
      const viewport = new Viewport({
        screenWidth: canvasWidth,
        screenHeight: canvasHeight,
        worldWidth: gridWidth * GRID_SIZE * GAME_GRID.VIEWPORT_WORLD_GRID_MULTIPLIER,
        worldHeight: gridHeight * GRID_SIZE * GAME_GRID.VIEWPORT_WORLD_GRID_MULTIPLIER,
        events: app.renderer.events, // PixiJS 8 events system
        passiveWheel: false, // Allow wheel to zoom instead of scroll
      });

      viewportRef.current = viewport;
      app.stage.addChild(viewport);
      
      // Enable viewport to receive events
      viewport.eventMode = 'static';

      // Detect if device has touch capability
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Enable viewport plugins for smooth trackpad/touch support
      const dragPlugin = viewport.drag({
        // On touch devices, allow single-finger drag for panning
        // On desktop, use right-click for panning
        mouseButtons: isTouchDevice ? 'all' : 'right',
        wheel: false, // Don't let drag plugin handle wheel
        // pressDrag must be true (or omitted) for drag to work!
        // Coin dragging won't interfere because coins stop event propagation
        // On touch devices, require a slight movement before starting drag
        // This helps distinguish between tap (coin selection) and drag (panning)
        underflow: isTouchDevice ? 'center' : 'top-left',
        // Disable context menu during right-click drag
        disableOnContextMenu: true,
      });
      
      // Debug: Log drag plugin configuration
      console.log('[GameGrid2] Drag plugin configured:', {
        mouseButtons: dragPlugin.options.mouseButtons,
        isTouchDevice,
        paused: dragPlugin.paused
      });
      
      // Debug: Add event listeners to test if right-click events are reaching viewport
      viewport.on('rightdown', (e) => {
        console.log('[GameGrid2] Viewport received rightdown event', e.button);
      });
      viewport.on('pointerdown', (e) => {
        console.log('[GameGrid2] Viewport received pointerdown event', e.button);
      });
      
      viewport
        .pinch({
          noDrag: false, // Allow two-finger dragging (panning)
          percent: GAME_GRID.VIEWPORT_PINCH_PERCENT,
          factor: GAME_GRID.VIEWPORT_PINCH_FACTOR,
          center: null, // Use center of two fingers as zoom center
        })
        .wheel({
          percent: GAME_GRID.VIEWPORT_WHEEL_PERCENT,
          smooth: GAME_GRID.VIEWPORT_WHEEL_SMOOTH,
          interrupt: true, // Allow wheel to interrupt ongoing zoom
          reverse: false, // Normal zoom direction
          center: null, // Zoom at mouse/touch position
          lineHeight: GAME_GRID.VIEWPORT_WHEEL_LINE_HEIGHT,
          trackpadPinch: true, // Enable trackpad pinch-to-zoom (Ctrl/Cmd + wheel)
          wheelZoom: true, // Enable mouse wheel zoom
        })
        .decelerate({
          friction: GAME_GRID.VIEWPORT_DECELERATE_FRICTION,
        })
        .clampZoom({
          minScale: GAME_GRID.VIEWPORT_MIN_SCALE,
          maxScale: GAME_GRID.VIEWPORT_MAX_SCALE,
        });

      // Add custom wheel handler to intercept trackpad two-finger swipes for panning
      // This runs BEFORE the wheel plugin processes the event
      const handleTrackpadSwipe = (e) => {
        // If Ctrl/Cmd is pressed, this is pinch-to-zoom - let wheel plugin handle it
        if (e.ctrlKey || e.metaKey) {
          return; // Let wheel plugin zoom
        }

        // Detect trackpad two-finger swipe vs mouse wheel
        // Trackpads use pixel mode (deltaMode = 0) with smooth, small values
        // Mouse wheels use line mode (deltaMode = 1) OR pixel mode with large, discrete values
        const isPixelMode = e.deltaMode === 0;
        const hasHorizontalMovement =
          Math.abs(e.deltaX) > GAME_GRID.TRACKPAD_HORIZONTAL_DELTA_THRESHOLD;

        // Trackpads produce smaller, more frequent delta values (typically < 50 pixels)
        // Mouse wheels produce larger, discrete values (typically > 50 pixels even in pixel mode)
        const hasSmallDelta =
          Math.abs(e.deltaY) < GAME_GRID.TRACKPAD_SMALL_DELTA_PIXEL_THRESHOLD;
        
        // Identify trackpad: pixel mode + (small deltas OR any horizontal movement)
        // This catches both vertical and horizontal trackpad swipes while excluding mouse wheels
        const isTrackpadSwipe = isPixelMode && (hasSmallDelta || hasHorizontalMovement);

        if (isTrackpadSwipe) {
          // This is a trackpad swipe - use for panning in BOTH directions
          e.preventDefault();
          e.stopPropagation();
          
          // Pan the viewport with both deltaX and deltaY for diagonal movement
          const currentX = viewport.center.x;
          const currentY = viewport.center.y;
          viewport.moveCenter(currentX + e.deltaX, currentY + e.deltaY);
          
          // Trigger deceleration for smooth momentum
          if (viewport.plugins.get('decelerate')) {
            viewport.plugins.get('decelerate').activate();
          }
          
          return false; // Prevent wheel plugin from processing
        }

        // Otherwise, let wheel plugin handle it for mouse wheel zoom
      };

      // Attach to canvas with capture phase to intercept before wheel plugin
      app.canvas.addEventListener('wheel', handleTrackpadSwipe, { 
        passive: false, 
        capture: true 
      });

      // Store cleanup function
      viewport._trackpadCleanup = () => {
        app.canvas.removeEventListener('wheel', handleTrackpadSwipe, { capture: true });
      };

      // Listen to zoom changes and notify parent (throttled for performance)
      viewport.on('zoomed', () => {
        if (onZoomChange) {
          // Clear any pending update
          if (zoomUpdateTimeoutRef.current) {
            clearTimeout(zoomUpdateTimeoutRef.current);
          }
          
          zoomUpdateTimeoutRef.current = setTimeout(() => {
            onZoomChange(viewport.scaled);
          }, GAME_GRID.ZOOM_UPDATE_THROTTLE_MS);
        }
      });

      // Listen to drag events to update cursor during panning
      viewport.on('drag-start', () => {
        setIsPanning(true);
      });
      viewport.on('drag-end', () => {
        setIsPanning(false);
      });

      // Store deselect handler reference
      viewport._deselectHandler = null;

      // Create coins container (will be added after grid)
      coinsContainerRef.current = new PIXI.Container();
      coinsContainerRef.current.sortableChildren = true;
      
      // Create overlay containers for health bars and names (rendered above all coins)
      healthOverlayRef.current = new PIXI.Container();
      nameOverlayRef.current = new PIXI.Container();
      coinActionsOverlayRef.current = new PIXI.Container();
      
      // Create paint interaction layer
      paintLayerRef.current = new PIXI.Container();
      paintLayerRef.current.eventMode = 'static';

      // Mark viewport as ready
      setIsViewportReady(true);
    };

    initApp();

    // Handle window resize
    const handleResize = () => {
      if (appRef.current && containerRef.current && viewportRef.current) {
        const newWidth = containerRef.current.clientWidth;
        const newHeight = containerRef.current.clientHeight;
        appRef.current.renderer.resize(newWidth, newHeight);
        viewportRef.current.resize(newWidth, newHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
      setIsViewportReady(false);
      
      // Clear any pending zoom updates
      if (zoomUpdateTimeoutRef.current) {
        clearTimeout(zoomUpdateTimeoutRef.current);
      }
      
      // Clean up trackpad handler
      if (viewportRef.current?._trackpadCleanup) {
        viewportRef.current._trackpadCleanup();
      }
      
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
        viewportRef.current = null;
      }
    };
  }, []); // Initialize only once

  // Grid size changes - reset viewport
  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.moveCorner(0, 0);
      viewportRef.current.setZoom(GAME_GRID.VIEWPORT_DEFAULT_ZOOM, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridWidth, gridHeight]);

  // Apply zoom to viewport (sync external zoom changes)
  useEffect(() => {
    if (viewportRef.current && viewportRef.current.scaled !== zoom) {
      viewportRef.current.setZoom(zoom, true);
    }
  }, [zoom]);

  // Prevent context menu on right-click (viewport handles panning)
  useEffect(() => {
    if (!appRef.current?.canvas) return;

    const canvas = appRef.current.canvas;
    
    const handleContextMenu = (e) => {
      e.preventDefault(); // Prevent right-click menu but allow pointer events
      return false;
    };

    // Prevent context menu on the canvas only
    canvas.addEventListener('contextmenu', handleContextMenu, false);

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu, false);
    };
  }, [isViewportReady]); // Re-run when viewport is ready to ensure canvas exists

  // Arrow key navigation for panning
  useEffect(() => {
    if (!viewportRef.current) return;

    const handleKeyDown = (e) => {
      // Only handle arrow keys and don't interfere with input fields
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const viewport = viewportRef.current;
      if (!viewport) return;

      e.preventDefault(); // Prevent page scrolling

      const currentX = viewport.center.x;
      const currentY = viewport.center.y;

      switch (e.key) {
        case 'ArrowUp':
          viewport.moveCenter(currentX, currentY - GAME_GRID.VIEWPORT_ARROW_PAN_SPEED);
          break;
        case 'ArrowDown':
          viewport.moveCenter(currentX, currentY + GAME_GRID.VIEWPORT_ARROW_PAN_SPEED);
          break;
        case 'ArrowLeft':
          viewport.moveCenter(currentX - GAME_GRID.VIEWPORT_ARROW_PAN_SPEED, currentY);
          break;
        case 'ArrowRight':
          viewport.moveCenter(currentX + GAME_GRID.VIEWPORT_ARROW_PAN_SPEED, currentY);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Draw grid when colors change (wait for textures to load)
  useEffect(() => {
    if (!isViewportReady || !viewportRef.current || !texturesLoaded || !appRef.current) return;

    viewportRef.current.removeChildren();
    drawGrid(
      viewportRef.current,
      appRef.current,
      gridWidth, 
      gridHeight, 
      isCreator, 
      canManageEnemies,
      canPaint,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      texturesRef.current,
      selectedBattlemapId,
      selectedObjectId,
      onSelectObject,
      isPaintMode,
      cursorMode,
      onObjectDragEnd,
      zoom,
      onDeselectAll
    );
    
    // Add paint interaction layer
    if (paintLayerRef.current) {
      paintLayerRef.current.removeChildren();
      setupPaintLayer(
        paintLayerRef.current,
        gridWidth,
        gridHeight,
        isPaintMode,
        paintMode,
        selectedPaintColor,
        areaPaintStart,
        areaPaintEnd,
        wallHoverCorner,
        wallDragStartCorner,
        wallDragEndCorner,
        onGridMouseDown,
        onGridMouseEnter,
        onGridMouseUp,
        isPaintingRef,
        viewportRef,
        zoom,
        onDeselectAll
      );
      viewportRef.current.addChild(paintLayerRef.current);
    }
    
    // Re-add coins and overlay containers after grid (in order: coins, health, names, coin actions)
    if (coinsContainerRef.current) {
      viewportRef.current.addChild(coinsContainerRef.current);
    }
    if (healthOverlayRef.current) {
      viewportRef.current.addChild(healthOverlayRef.current);
    }
    if (nameOverlayRef.current) {
      viewportRef.current.addChild(nameOverlayRef.current);
    }
    if (coinActionsOverlayRef.current) {
      viewportRef.current.addChild(coinActionsOverlayRef.current);
    }
  }, [isViewportReady, texturesLoaded, battlemapLoadTick, gridColors, gridWalls, gridObjects, gridObjectInstances, gridWidth, gridHeight, isCreator, canManageEnemies, canPaint, isPaintMode, paintMode, selectedPaintColor, areaPaintStart, areaPaintEnd, wallHoverCorner, wallDragStartCorner, wallDragEndCorner, onGridMouseDown, onGridMouseEnter, onGridMouseUp, zoom, selectedBattlemapId, selectedObjectId, onSelectObject, cursorMode, onObjectDragEnd, onDeselectAll, onCoinHealthAction, onCoinStatusAction, onCoinRemoveAction, onCoinDeleteAction, onCoinStatBlockAction]);

  // Update coins
  useEffect(() => {
    if (!isViewportReady || !appRef.current || !coinsContainerRef.current) return;

    const coinsContainer = coinsContainerRef.current;
    coinsContainer.removeChildren();
    
    // Clear overlay containers
    if (healthOverlayRef.current) healthOverlayRef.current.removeChildren();
    if (nameOverlayRef.current) nameOverlayRef.current.removeChildren();
    if (coinActionsOverlayRef.current) coinActionsOverlayRef.current.removeChildren();

    // Helper to check if a coin is in a fogged cell
    const isCoinInFog = (coinX, coinY, coinSize) => {
      // Check all cells the coin occupies (for large coins that span multiple cells)
      const gridI = Math.floor(coinX / GRID_SIZE);
      const gridJ = Math.floor(coinY / GRID_SIZE);
      
      for (
        let di = 0;
        di < (coinSize > GRID_SIZE ? GAME_GRID.LARGE_TOKEN_GRID_CELLS : 1);
        di++
      ) {
        for (
          let dj = 0;
          dj < (coinSize > GRID_SIZE ? GAME_GRID.LARGE_TOKEN_GRID_CELLS : 1);
          dj++
        ) {
          const cellKey = `${gridI + di}-${gridJ + dj}`;
          const cellData = gridColors[cellKey];
          if (cellData) {
            const hasFog = typeof cellData === 'object' ? cellData?.fog : cellData === 'fog';
            if (hasFog) return true;
          }
        }
      }
      return false;
    };

    coins.forEach(coin => {
      // Skip removed coins (they're not on the grid)
      if (coin.removed || coin.x === undefined || coin.y === undefined) {
        return;
      }

      // Skip hidden coins for non-creators
      if (coin.hidden && !isCreator) {
        return;
      }

      const coinSize = coin.isLarge ? GRID_SIZE * GAME_GRID.LARGE_TOKEN_GRID_CELLS : GRID_SIZE;

      // Check if coin is hidden by fog (non-creators can't see coins in fogged cells)
      if (!isCreator && isCoinInFog(coin.x, coin.y, coinSize)) {
        return; // Don't render coins hidden by fog
      }
      const radius = (coinSize / 2) - GAME_GRID.TOKEN_RADIUS_INSET;
      const isSelected = selectedCoinId === coin.id;
      const currentHealth = coin.currentHealth ?? coin.maxHealth;
      const isDead = coin.maxHealth && currentHealth <= 0;
      const isMapMode = cursorMode === CURSOR_MODE.MAP;
      const coinCenterX = coin.x + coinSize / 2;
      const coinCenterY = coin.y + coinSize / 2;

      // Create coin container for grouping
      const coinContainer = new PIXI.Container();
      coinContainer.x = coin.x + coinSize / 2;
      coinContainer.y = coin.y + coinSize / 2;
      coinContainer.coinData = coin; // Store reference to coin data
      // Selected coin (and its context buttons) must paint above overlapping coins
      coinContainer.zIndex = isSelected ? GAME_GRID.Z_INDEX_SELECTED : GAME_GRID.Z_INDEX_NORMAL;

      // Apply transparency for hidden coins (only visible to creator)
      if (coin.hidden) {
        coinContainer.alpha = GAME_GRID.TOKEN_HIDDEN_ALPHA;
      } else if (isDead) {
        coinContainer.alpha = GAME_GRID.TOKEN_DEAD_ALPHA;
      }

      // Selection highlight (behind everything)
      if (isSelected) {
        const selectionHighlight = new PIXI.Graphics();
        selectionHighlight.roundRect(
          -coinSize / 2 - GAME_GRID.TOKEN_SELECTION_OUTSET,
          -coinSize / 2 - GAME_GRID.TOKEN_SELECTION_OUTSET,
          coinSize + GAME_GRID.TOKEN_SELECTION_SIZE_PAD,
          coinSize + GAME_GRID.TOKEN_SELECTION_SIZE_PAD,
          GAME_GRID.TOKEN_SELECTION_CORNER_RADIUS
        );
        selectionHighlight.stroke({
          width: GAME_GRID.TOKEN_SELECTION_STROKE_WIDTH,
          color: GAME_GRID.COLOR_ACCENT_GOLD,
          alpha: GAME_GRID.TOKEN_SELECTION_STROKE_ALPHA,
        });
        coinContainer.addChild(selectionHighlight);
      }

      // Shadow
      const shadow = new PIXI.Graphics();
      shadow.circle(GAME_GRID.TOKEN_SHADOW_OFFSET_X, GAME_GRID.TOKEN_SHADOW_OFFSET_Y, radius);
      shadow.fill({ color: GAME_GRID.COLOR_BLACK, alpha: GAME_GRID.TOKEN_SHADOW_ALPHA });
      coinContainer.addChild(shadow);

      // Main coin circle
      const coinGraphics = new PIXI.Graphics();
      const baseColor = colorToHex(coin.color || GAME_GRID.DEFAULT_TOKEN_COLOR_HEX_STRING);
      
      // Gradient-like effect with layered circles
      coinGraphics.circle(0, 0, radius);
      coinGraphics.fill({ color: baseColor, alpha: 1 });

      // Border
      coinGraphics.circle(0, 0, radius);
      coinGraphics.stroke({
        width: GAME_GRID.TOKEN_CIRCLE_STROKE_WIDTH,
        color: darkenColor(baseColor, GAME_GRID.TOKEN_BORDER_DARKEN_AMOUNT),
        alpha: 1,
      });

      coinContainer.addChild(coinGraphics);

      // Enemy glow effect using filter
      if (coin.isEnemy) {
        const glowFilter = new GlowFilter({
          distance: GAME_GRID.GLOW_DISTANCE,
          outerStrength: GAME_GRID.GLOW_OUTER_STRENGTH,
          innerStrength: GAME_GRID.GLOW_INNER_STRENGTH,
          color: GAME_GRID.COLOR_ENEMY_GLOW,
          quality: GAME_GRID.GLOW_QUALITY,
        });
        coinContainer.filters = [glowFilter];
      }

      // Add image if coin has one
      if (coin.image) {
        // PixiJS 8: Load texture asynchronously
        PIXI.Assets.load(coin.image).then((texture) => {
          const sprite = new PIXI.Sprite(texture);
          sprite.anchor.set(0.5);
          sprite.width = radius * GAME_GRID.TOKEN_SPRITE_RADIUS_SCALE;
          sprite.height = radius * GAME_GRID.TOKEN_SPRITE_RADIUS_SCALE;

          // Create circular mask
          const mask = new PIXI.Graphics();
          mask.circle(0, 0, radius - GAME_GRID.TOKEN_MASK_RADIUS_INSET);
          mask.fill({ color: GAME_GRID.COLOR_WHITE, alpha: 1 });
          coinContainer.addChild(mask);
          sprite.mask = mask;
          
          coinContainer.addChild(sprite);
        }).catch((e) => {
          console.warn('Failed to load coin image', coin.image, e);
        });
      }

      // Name label - added to name overlay container (above all coins)
      if (coin.name && nameOverlayRef.current) {
        const nameContainer = new PIXI.Container();
        nameContainer.x = coinCenterX;
        nameContainer.y = coinCenterY;
        
        const nameText = new PIXI.Text(
          coin.name.slice(
            0,
            coin.isLarge ? GAME_GRID.NAME_MAX_CHARS_LARGE : GAME_GRID.NAME_MAX_CHARS_SMALL
          ),
          {
            fontFamily: 'Arial',
            fontSize: coin.isLarge ? GAME_GRID.NAME_FONT_SIZE_LARGE : GAME_GRID.NAME_FONT_SIZE_SMALL,
            fill: GAME_GRID.COLOR_WHITE,
            fontWeight: 'bold',
            resolution: GAME_GRID.NAME_TEXT_RESOLUTION,
          }
        );
        nameText.anchor.set(0.5, 0);
        nameText.y = radius + GAME_GRID.NAME_LABEL_OFFSET_Y;

        // Add rounded background box for better contrast
        const padding = GAME_GRID.NAME_PADDING;
        const bgWidth = nameText.width + padding * 2;
        const bgHeight = nameText.height + padding;
        const nameBg = new PIXI.Graphics();
        nameBg.roundRect(
          -bgWidth / 2,
          radius + GAME_GRID.NAME_BG_OFFSET_Y,
          bgWidth,
          bgHeight,
          GAME_GRID.NAME_BG_CORNER_RADIUS
        );
        nameBg.fill({ color: GAME_GRID.COLOR_BLACK, alpha: GAME_GRID.NAME_BG_ALPHA });
        
        nameContainer.addChild(nameBg);
        nameContainer.addChild(nameText);
        nameOverlayRef.current.addChild(nameContainer);
      }

      // Health bar - added to health overlay container (above all coins)
      // Check if player can see this coin's health
      const canSeeHealth = isCreator || (!coin.isEnemy || permissions.allowEnemyHealthView);
      
      if (coin.maxHealth && healthOverlayRef.current && canSeeHealth) {
        const healthContainer = new PIXI.Container();
        healthContainer.x = coinCenterX;
        healthContainer.y = coinCenterY;
        
        const barWidth = coin.isLarge
          ? GRID_SIZE * GAME_GRID.HEALTH_BAR_WIDTH_MULT_LARGE
          : GRID_SIZE * GAME_GRID.HEALTH_BAR_WIDTH_MULT_SMALL;
        const barHeight = GAME_GRID.HEALTH_BAR_HEIGHT;
        const healthPercent = Math.max(0, Math.min(1, (coin.currentHealth ?? coin.maxHealth) / coin.maxHealth));
        const barY = -radius - barHeight - GAME_GRID.HEALTH_BAR_GAP;

        // Health background
        const healthBackground = new PIXI.Graphics();
        healthBackground.roundRect(
          -barWidth / 2,
          barY,
          barWidth,
          barHeight,
          GAME_GRID.HEALTH_BAR_CORNER_RADIUS
        );
        healthBackground.fill({ color: GAME_GRID.COLOR_BLACK, alpha: GAME_GRID.HEALTH_BG_ALPHA });
        healthContainer.addChild(healthBackground);

        // Health fill
        const healthFill = new PIXI.Graphics();
        const healthColor =
          healthPercent > GAME_GRID.HEALTH_THRESHOLD_HIGH
            ? GAME_GRID.COLOR_HEALTH_HIGH
            : healthPercent > GAME_GRID.HEALTH_THRESHOLD_LOW
              ? GAME_GRID.COLOR_HEALTH_MID
              : GAME_GRID.COLOR_HEALTH_LOW;
        healthFill.roundRect(
          -barWidth / 2,
          barY,
          barWidth * healthPercent,
          barHeight,
          GAME_GRID.HEALTH_BAR_CORNER_RADIUS
        );
        healthFill.fill({ color: healthColor, alpha: GAME_GRID.HEALTH_FILL_ALPHA });
        healthContainer.addChild(healthFill);

        // Health text
        const healthText = new PIXI.Text(`${coin.currentHealth ?? coin.maxHealth}/${coin.maxHealth}`, {
          fontFamily: 'Arial',
          fontWeight: GAME_GRID.HEALTH_TEXT_FONT_WEIGHT,
          fontSize: GAME_GRID.HEALTH_TEXT_FONT_SIZE,
          fill: GAME_GRID.COLOR_WHITE,
          resolution: GAME_GRID.HEALTH_TEXT_RESOLUTION,
        });
        healthText.anchor.set(0.5);
        healthText.y = barY + barHeight / 2;
        healthContainer.addChild(healthText);
        
        healthOverlayRef.current.addChild(healthContainer);
      }

      // AC (Armor Class) display - shield icon with number
      // Uses same visibility rules as health
      const canSeeAC = isCreator || (!coin.isEnemy || permissions.allowEnemyHealthView);
      
      if (coin.armorClass && healthOverlayRef.current && canSeeAC) {
        const acContainer = new PIXI.Container();
        acContainer.x = coinCenterX;
        acContainer.y = coinCenterY;
        
        const shieldSize = coin.isLarge ? GAME_GRID.SHIELD_SIZE_LARGE : GAME_GRID.SHIELD_SIZE_SMALL;
        const shieldX = radius + GAME_GRID.SHIELD_OFFSET_X;
        const shieldY = -radius / 2;
        
        // Draw shield shape
        const shield = new PIXI.Graphics();
        shield.moveTo(shieldX, shieldY - shieldSize * 0.5);
        shield.lineTo(shieldX + shieldSize * 0.5, shieldY - shieldSize * 0.35);
        shield.lineTo(shieldX + shieldSize * 0.5, shieldY + shieldSize * 0.1);
        shield.quadraticCurveTo(shieldX + shieldSize * 0.5, shieldY + shieldSize * 0.4, shieldX, shieldY + shieldSize * 0.55);
        shield.quadraticCurveTo(shieldX - shieldSize * 0.5, shieldY + shieldSize * 0.4, shieldX - shieldSize * 0.5, shieldY + shieldSize * 0.1);
        shield.lineTo(shieldX - shieldSize * 0.5, shieldY - shieldSize * 0.35);
        shield.closePath();
        shield.fill({ color: GAME_GRID.COLOR_SHIELD_FILL, alpha: GAME_GRID.SHIELD_FILL_ALPHA });
        shield.stroke({ color: GAME_GRID.COLOR_SHIELD_STROKE, width: GAME_GRID.AC_SHIELD_STROKE_WIDTH });
        acContainer.addChild(shield);
        
        // AC text
        const acText = new PIXI.Text(coin.armorClass.toString(), {
          fontFamily: 'Arial',
          fontWeight: 'bold',
          fontSize: coin.isLarge ? GAME_GRID.AC_FONT_SIZE_LARGE : GAME_GRID.AC_FONT_SIZE_SMALL,
          fill: GAME_GRID.COLOR_WHITE,
          resolution: GAME_GRID.AC_TEXT_RESOLUTION,
        });
        acText.anchor.set(0.5);
        acText.x = shieldX;
        acText.y = shieldY;
        acContainer.addChild(acText);
        
        healthOverlayRef.current.addChild(acContainer);
      }

      // Status icons - displayed on bottom left of token (mirroring AC position)
      if (coin.statuses && coin.statuses.length > 0 && healthOverlayRef.current) {
        const statusContainer = new PIXI.Container();
        statusContainer.x = coinCenterX;
        statusContainer.y = coinCenterY;
        
        const iconSize = coin.isLarge ? GAME_GRID.STATUS_ICON_SIZE_LARGE : GAME_GRID.STATUS_ICON_SIZE_SMALL;
        const iconSpacing = GAME_GRID.STATUS_ICON_SPACING;
        const baseX = -radius - iconSize + GAME_GRID.STATUS_BASE_OFFSET;
        const startY = radius / 2; // Start at middle-bottom area
        
        coin.statuses.forEach((status, index) => {
          const iconY = startY - (index * (iconSize + iconSpacing)); // Subtract to move upward
          
          // Get status icon from imported constants
          const statusImagePath = STATUS_ICONS[status];
          
          if (!statusImagePath) {
            console.warn('Status icon not found for:', status);
            return;
          }
          
          // Load and display status icon
          PIXI.Assets.load(statusImagePath).then((texture) => {
            const statusSprite = new PIXI.Sprite(texture);
            statusSprite.width = iconSize;
            statusSprite.height = iconSize;
            statusSprite.x = baseX;
            statusSprite.y = iconY;
            
            // Add semi-transparent background for better visibility
            const statusBg = new PIXI.Graphics();
            statusBg.roundRect(baseX, iconY, iconSize, iconSize, GAME_GRID.STATUS_BG_CORNER_RADIUS);
            statusBg.fill({ color: GAME_GRID.COLOR_BLACK, alpha: GAME_GRID.STATUS_BG_ALPHA });
            statusContainer.addChild(statusBg);
            
            statusContainer.addChild(statusSprite);
          }).catch((e) => {
            console.warn('Failed to load status icon', status, e);
          });
        });
        
        healthOverlayRef.current.addChild(statusContainer);
      }

      // Context action icons for selected coin (top overlay so they sit above health/name)
      if (
        coinActionsOverlayRef.current &&
        isSelected &&
        !isPaintMode &&
        !isMapMode
      ) {
        const actionsRoot = new PIXI.Container();
        actionsRoot.x = coinCenterX;
        actionsRoot.y = coinCenterY;

        const iconSize = GAME_GRID.CONTEXT_ACTION_ICON_SIZE;
        const iconSpacing = GAME_GRID.CONTEXT_ACTION_ICON_SPACING;
        const startX = radius + GAME_GRID.CONTEXT_ACTION_START_OFFSET_X;
        let currentY = -radius;

        const createContextIcon = (iconPath, color, onClick) => {
          const iconContainer = new PIXI.Container();
          iconContainer.x = startX;
          iconContainer.y = currentY;

          const circle = new PIXI.Graphics();
          circle.circle(0, 0, iconSize / 2);
          circle.fill({ color: color, alpha: GAME_GRID.CONTEXT_ACTION_CIRCLE_ALPHA });
          iconContainer.addChild(circle);

          PIXI.Assets.load(iconPath).then((texture) => {
            const iconSprite = new PIXI.Sprite(texture);
            iconSprite.anchor.set(0.5);
            iconSprite.width = iconSize * GAME_GRID.CONTEXT_ACTION_SPRITE_SCALE;
            iconSprite.height = iconSize * GAME_GRID.CONTEXT_ACTION_SPRITE_SCALE;
            iconContainer.addChild(iconSprite);
          }).catch((e) => {
            console.warn('Failed to load context icon', iconPath, e);
          });

          iconContainer.eventMode = 'static';
          iconContainer.cursor = 'pointer';
          iconContainer.on('pointerdown', (e) => {
            e.stopPropagation();
            onClick(coin);
          });

          iconContainer.on('pointerenter', () => {
            iconContainer.scale.set(GAME_GRID.CONTEXT_ICON_HOVER_SCALE);
          });
          iconContainer.on('pointerleave', () => {
            iconContainer.scale.set(GAME_GRID.CONTEXT_ICON_NORMAL_SCALE);
          });

          actionsRoot.addChild(iconContainer);
          currentY += iconSize + iconSpacing;
        };

        if (onCoinHealthAction) {
          createContextIcon(heartIcon, GAME_GRID.COLOR_CONTEXT_HEALTH, onCoinHealthAction);
        }
        if (onCoinStatusAction) {
          createContextIcon(statusIcon, GAME_GRID.COLOR_CONTEXT_STATUS, onCoinStatusAction);
        }
        if (coin.isEnemy) {
          if (coin.monsterData && onCoinStatBlockAction) {
            createContextIcon(bookIcon, GAME_GRID.COLOR_CONTEXT_STATBLOCK, onCoinStatBlockAction);
          }
          if (onCoinDeleteAction) {
            createContextIcon(trashIcon, GAME_GRID.COLOR_CONTEXT_REMOVE, onCoinDeleteAction);
          }
        } else if (onCoinRemoveAction) {
          createContextIcon(removeIcon, GAME_GRID.COLOR_CONTEXT_REMOVE, onCoinRemoveAction);
        }

        coinActionsOverlayRef.current.addChild(actionsRoot);
      }

      // Make interactive only in Token Layer mode (not in paint mode or Map Layer mode)
      const canDrag = isCreator || !coin.isEnemy || canManageEnemies;
      
      // Disable interaction in paint mode or Map Layer mode
      if (isPaintMode) {
        coinContainer.eventMode = 'none';
        coinContainer.alpha = GAME_GRID.TOKEN_PAINT_MODE_ALPHA;
      } else if (isMapMode) {
        // In Map Layer mode, tokens are NOT interactive but stay fully visible
        coinContainer.eventMode = 'none';
      } else {
        // In Token Layer mode, tokens are fully interactive
        coinContainer.eventMode = 'static';
        coinContainer.cursor = canDrag ? 'grab' : 'pointer';
      }

      // Use shared drag handler
      createDragHandler({
        container: coinContainer,
        viewport: viewportRef.current,
        entityId: coin.id,
        selectedId: selectedCoinId,
        canDrag: canDrag,
        onSelect: onSelectCoin,
        onDragEnd: (container, startPos) => {
          // Restore shadow
          shadow.visible = true;
          
          // Snap to grid
          const snapped = snapToGrid(
            container.x - coinSize / 2,
            container.y - coinSize / 2
          );
          
          // Check if the snapped position is within valid grid bounds
          validateCoinPosition(container, coin, startPos, snapped, coinSize);
        },
        visualEffects: {
          onDragStart: () => {
            shadow.visible = false;
          },
          dragCursor: 'grabbing',
          normalCursor: 'grab',
          dragAlpha: GAME_GRID.TOKEN_DRAG_ALPHA,
          normalAlpha: 1,
        }
      });

      coinsContainer.addChild(coinContainer);
    });
  }, [isViewportReady, coins, isCreator, canManageEnemies, gridWidth, gridColors, snapToGrid, isWithinBounds, onCoinDragEnd, selectedCoinId, onSelectCoin, isPaintMode, cursorMode, validateCoinPosition, permissions, onCoinHealthAction, onCoinStatusAction, onCoinRemoveAction, onCoinDeleteAction, onCoinStatBlockAction]);

  // Update area paint preview
  useEffect(() => {
    if (!isViewportReady || !appRef.current || !paintLayerRef.current) return;
    if (!isPaintMode || paintMode !== PAINT_MODE.AREA) return;
    
    // Find or create area preview graphics
    let areaPreview = paintLayerRef.current.children.find(child => child.name === 'areaPreview');
    if (!areaPreview) {
      areaPreview = new PIXI.Graphics();
      areaPreview.name = 'areaPreview';
      paintLayerRef.current.addChild(areaPreview);
    }

    areaPreview.clear();
    
    if (areaPaintStart && areaPaintEnd) {
      const minI = Math.min(areaPaintStart.i, areaPaintEnd.i);
      const maxI = Math.max(areaPaintStart.i, areaPaintEnd.i);
      const minJ = Math.min(areaPaintStart.j, areaPaintEnd.j);
      const maxJ = Math.max(areaPaintStart.j, areaPaintEnd.j);
      
      const x = minI * GRID_SIZE;
      const y = minJ * GRID_SIZE;
      const width = (maxI - minI + 1) * GRID_SIZE;
      const height = (maxJ - minJ + 1) * GRID_SIZE;
      
      // Draw semi-transparent rectangle
      areaPreview.rect(x, y, width, height);
      areaPreview.fill({ color: GAME_GRID.COLOR_ACCENT_GOLD, alpha: GAME_GRID.AREA_PREVIEW_FILL_ALPHA });
      areaPreview.stroke({
        width: GAME_GRID.AREA_PREVIEW_STROKE_WIDTH,
        color: GAME_GRID.COLOR_ACCENT_GOLD,
        alpha: 1,
      });
    }
  }, [isViewportReady, isPaintMode, paintMode, areaPaintStart, areaPaintEnd]);

  // Determine cursor based on mode
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isPaintMode) {
      const isEraseTool = typeof selectedPaintColor === 'object' && (
        selectedPaintColor.clearColor ||
        selectedPaintColor.clearFog
      );
      return isEraseTool ? GRID_ERASE_CURSOR : GRID_PAINT_CURSOR;
    }
    return 'default';
  };

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'transparent',
        overflow: 'hidden',
      }}
      onMouseUp={() => {
        isPaintingRef.current = false;
        onGridMouseUp?.();
      }}
      onMouseLeave={() => {
        isPaintingRef.current = false;
        onGridMouseLeave?.();
      }}
    >
      <div 
        className="pixi-canvas-wrapper" 
        style={{ 
          cursor: getCursor(),
          width: '100%',
          height: '100%',
          pointerEvents: 'auto', // Ensure pointer events are enabled
          touchAction: 'none', // Prevent default touch actions
        }}
      />
    </div>
  );
});

GameGrid.displayName = 'GameGrid';

function drawGrid(viewport, app, gridWidth, gridHeight, isCreator, canManageEnemies, canPaint, gridColors = {}, gridWalls = {}, gridObjects = {}, gridObjectInstances = {}, textures = {}, selectedBattlemapId = GAME_GRID.BATTLEMAP_NONE, selectedObjectId = null, onSelectObject = null, isPaintMode = false, cursorMode = null, onObjectDragEnd = null, zoom = GAME_GRID.VIEWPORT_DEFAULT_ZOOM, onDeselectAll = null) {
  console.log('[drawGrid] Called with selectedBattlemapId:', selectedBattlemapId, 'textures keys:', Object.keys(textures));
  
  // Remove old deselect handler if it exists
  if (viewport._deselectHandler) {
    viewport.off('pointerdown', viewport._deselectHandler);
    viewport._deselectHandler = null;
  }
  
  // Add viewport-level click handler for deselecting
  if (!isPaintMode && onDeselectAll) {
    const deselectHandler = (event) => {
      // Only handle left click
      if (event.button === GAME_GRID.POINTER_BUTTON_PRIMARY) {
        // Check if the click was on the viewport background (not on a coin/object)
        // If target is viewport itself, deselect
        if (event.target === viewport || event.target.parent === viewport) {
          onDeselectAll();
        }
      }
    };
    
    viewport.on('pointerdown', deselectHandler);
    viewport._deselectHandler = deselectHandler;
  }
  
  const graphics = new PIXI.Graphics();
  viewport.addChild(graphics);

  // Add battlemap background behind the grid
  if (selectedBattlemapId && selectedBattlemapId !== GAME_GRID.BATTLEMAP_NONE) {
    const battlemapTextureKey = `battlemap-${selectedBattlemapId}`;
    const battlemapTexture = textures[battlemapTextureKey];
    console.log('[drawGrid] Looking for battlemap texture:', battlemapTextureKey, 'found:', !!battlemapTexture);
    
    if (battlemapTexture) {
      try {
        const battlemap = getBattlemapById(selectedBattlemapId);
        if (battlemap) {
          const battlemapSprite = new PIXI.Sprite(battlemapTexture);
          battlemapSprite.x = 0;
          battlemapSprite.y = 0;
          // Scale the image to fit the specified grid dimensions
          battlemapSprite.width = battlemap.gridWidth * GRID_SIZE;
          battlemapSprite.height = battlemap.gridHeight * GRID_SIZE;
          battlemapSprite.alpha = GAME_GRID.BATTLEMAP_SPRITE_ALPHA;
          viewport.addChild(battlemapSprite);
          console.log('[drawGrid] Battlemap added to viewport:', battlemap.name);
        } else {
          console.warn('[drawGrid] Battlemap not found:', selectedBattlemapId);
        }
      } catch (e) {
        console.warn('[drawGrid] Failed to add battlemap sprite', e);
      }
    } else {
      console.warn('[drawGrid] Battlemap texture not loaded for:', battlemapTextureKey);
    }
  }

  // Container for cell content (textures and colors)
  const cellContainer = new PIXI.Container();
  viewport.addChild(cellContainer);

  // Collect fogged cells for unified fog rendering
  const foggedCells = [];

  // Draw grid cells with colors and textures
  for (let i = 0; i < gridWidth; i++) {
    for (let j = 0; j < gridHeight; j++) {
      const x = i * GRID_SIZE;
      const y = j * GRID_SIZE;
      const cellKey = `${i}-${j}`;
      const cellData = gridColors[cellKey];

      if (cellData) {
        const isFogOfWar = typeof cellData === 'object' ? cellData?.fog : cellData === 'fog';
        const baseColor = typeof cellData === 'object' ? cellData?.color : (cellData !== 'fog' ? cellData : null);

        // Draw base color/texture first
        if (baseColor) {
          const texture = textures[baseColor];
          if (texture) {
            try {
              // Use texture sprite
              const sprite = new PIXI.Sprite(texture);
              sprite.x = x;
              sprite.y = y;
              sprite.width = GRID_SIZE;
              sprite.height = GRID_SIZE;
              cellContainer.addChild(sprite);
            } catch (e) {
              // Fallback to solid color if texture fails
              const cellGraphics = new PIXI.Graphics();
              const hexColor = colorToHex(baseColor);
              cellGraphics.rect(x, y, GRID_SIZE, GRID_SIZE);
              cellGraphics.fill({ color: hexColor, alpha: 1 });
              cellContainer.addChild(cellGraphics);
            }
          } else {
            // Fallback to solid color
            const cellGraphics = new PIXI.Graphics();
            const hexColor = colorToHex(baseColor);
            cellGraphics.rect(x, y, GRID_SIZE, GRID_SIZE);
            cellGraphics.fill({ color: hexColor, alpha: 1 });
            cellContainer.addChild(cellGraphics);
          }
        }

        // Collect fog cells for unified rendering later
        if (isFogOfWar) {
          foggedCells.push({ i, j, x, y });
        }
      }
    }
  }

  // Render fog as a single solid Graphics shape to prevent any gaps
  if (foggedCells.length > 0) {
    const fogGraphics = new PIXI.Graphics();
    
    // Draw all fog cells as a single continuous path
    // This prevents any seams between cells
    fogGraphics.beginPath();
    for (const cell of foggedCells) {
      fogGraphics.rect(cell.x, cell.y, GRID_SIZE, GRID_SIZE);
    }
    
    const fogAlpha = isCreator ? GAME_GRID.FOG_CREATOR_ALPHA : GAME_GRID.FOG_PLAYER_ALPHA;
    fogGraphics.fill({ color: GAME_GRID.FOG_FILL_COLOR, alpha: fogAlpha });
    
    cellContainer.addChild(fogGraphics);
    
    // Draw diagonal line from top-right to bottom-left on each fog cell for visual distinction
    const fogLines = new PIXI.Graphics();
    for (const cell of foggedCells) {
      fogLines.moveTo(cell.x + GRID_SIZE, cell.y);
      fogLines.lineTo(cell.x, cell.y + GRID_SIZE);
    }
    fogLines.stroke({
      width: GAME_GRID.FOG_LINE_STROKE_WIDTH,
      color: GAME_GRID.FOG_LINE_COLOR,
      alpha: GAME_GRID.FOG_LINE_ALPHA,
    });
    cellContainer.addChild(fogLines);
    
    // Draw borders around fog cells for creator visibility
    if (isCreator) {
      const fogBorders = new PIXI.Graphics();
      for (const cell of foggedCells) {
        fogBorders.rect(cell.x, cell.y, GRID_SIZE, GRID_SIZE);
      }
      fogBorders.stroke({
        width: GAME_GRID.FOG_LINE_STROKE_WIDTH,
        color: GAME_GRID.FOG_LINE_COLOR,
        alpha: GAME_GRID.FOG_LINE_ALPHA,
      });
      cellContainer.addChild(fogBorders);
    }
  }

  // Helper to check if an object is in a fogged cell
  const isObjectInFog = (anchorX, anchorY, width, height, rotation) => {
    // Calculate rotated dimensions
    let displayWidth = width;
    let displayHeight = height;
    if (rotation === 90 || rotation === 270) {
      [displayWidth, displayHeight] = [displayHeight, displayWidth];
    }
    
    // Check all cells the object occupies
    for (let di = 0; di < displayWidth; di++) {
      for (let dj = 0; dj < displayHeight; dj++) {
        const cellKey = `${anchorX + di}-${anchorY + dj}`;
        const cellData = gridColors[cellKey];
        if (cellData) {
          const hasFog = typeof cellData === 'object' ? cellData?.fog : cellData === 'fog';
          if (hasFog) return true;
        }
      }
    }
    return false;
  };

  // Container for objects (above paint layer)
  const objectsContainer = new PIXI.Container();
  viewport.addChild(objectsContainer);

  // Draw objects using instances (multi-grid support)
  const renderedInstances = new Set();
  
  for (const [instanceId, instance] of Object.entries(gridObjectInstances)) {
    // Skip if already rendered
    if (renderedInstances.has(instanceId)) continue;
    renderedInstances.add(instanceId);
    
    // Check if object is hidden by fog (non-creators can't see objects in fogged cells)
    if (!isCreator && isObjectInFog(instance.anchorX, instance.anchorY, instance.width, instance.height, instance.rotation)) {
      continue; // Don't render objects hidden by fog
    }
    
    const texture = textures[instance.type];
    if (texture) {
      try {
        // Get object definition
        const objectDef = OBJECT_ITEMS.find(o => o.color === instance.type);
        if (!objectDef) continue;
        
        // Original logical dimensions (unrotated, used for placement/collision)
        const originalWidth = instance.width;
        const originalHeight = instance.height;

        // Render dimensions can differ from logical footprint (configured per object type)
        const renderWidth = objectDef.renderWidth ?? originalWidth;
        const renderHeight = objectDef.renderHeight ?? originalHeight;

        // Rotated dimensions/offset (for highlight bounds)
        let highlightX = 0;
        let highlightY = 0;
        let displayWidth = renderWidth;
        let displayHeight = renderHeight;
        if (instance.rotation === 90) {
          displayWidth = renderHeight;
          displayHeight = renderWidth;
          highlightX = originalHeight - renderHeight;
        } else if (instance.rotation === 180) {
          displayWidth = renderWidth;
          displayHeight = renderHeight;
          highlightX = originalWidth - renderWidth;
          highlightY = originalHeight - renderHeight;
        } else if (instance.rotation === 270) {
          displayWidth = renderHeight;
          displayHeight = renderWidth;
          highlightY = originalWidth - renderWidth;
        }
        
        // Create container for object (for selection highlight)
        const objectContainer = new PIXI.Container();
        objectContainer.x = instance.anchorX * GRID_SIZE;
        objectContainer.y = instance.anchorY * GRID_SIZE;
        
        // Selection highlight (behind sprite) - uses rotated dimensions
        const isSelected = selectedObjectId === instanceId;
        if (isSelected) {
          const highlight = new PIXI.Graphics();
          highlight.roundRect(
            highlightX * GRID_SIZE,
            highlightY * GRID_SIZE,
            displayWidth * GRID_SIZE,
            displayHeight * GRID_SIZE,
            GAME_GRID.OBJECT_HIGHLIGHT_CORNER_RADIUS
          );
          highlight.stroke({
            width: GAME_GRID.OBJECT_HIGHLIGHT_STROKE_WIDTH,
            color: GAME_GRID.COLOR_ACCENT_GOLD,
            alpha: GAME_GRID.OBJECT_HIGHLIGHT_STROKE_ALPHA,
          });
          highlight.fill({
            color: GAME_GRID.COLOR_ACCENT_GOLD,
            alpha: GAME_GRID.OBJECT_HIGHLIGHT_FILL_ALPHA,
          });
          objectContainer.addChild(highlight);
        }
        
        // Create sprite for the object
        const sprite = new PIXI.Sprite(texture);
        sprite.width = renderWidth * GRID_SIZE;
        sprite.height = renderHeight * GRID_SIZE;
        
        // Apply rotation if needed
        if (instance.rotation !== 0) {
          sprite.angle = instance.rotation;
          sprite.pivot.set(0, 0);
          
          // Adjust position based on rotation
          // The sprite rotates around its top-left corner (pivot 0,0)
          if (instance.rotation === 90) {
            // Align to logical footprint edge after rotation
            sprite.x = originalHeight * GRID_SIZE;
            sprite.y = 0;
          } else if (instance.rotation === 180) {
            // Align to logical footprint edge after rotation
            sprite.x = originalWidth * GRID_SIZE;
            sprite.y = originalHeight * GRID_SIZE;
          } else if (instance.rotation === 270) {
            // Align to logical footprint edge after rotation
            sprite.x = 0;
            sprite.y = originalWidth * GRID_SIZE;
          }
        }
        
        objectContainer.addChild(sprite);
        
        // Make interactive based on mode and permissions
        if (!isPaintMode && onSelectObject && cursorMode && canPaint) {
          const isMapMode = cursorMode === CURSOR_MODE.MAP;
          
          // Only enable interaction in MAP mode
          if (isMapMode) {
            objectContainer.eventMode = 'static';
            objectContainer.cursor = 'grab';
            
            // In MAP mode, objects are draggable (using shared drag handler)
            createDragHandler({
              container: objectContainer,
              viewport: viewport,
              entityId: instanceId,
              selectedId: selectedObjectId,
              canDrag: true,
              onSelect: onSelectObject,
              onDragEnd: (container) => {
                // Calculate grid position from container position
                const gridX = Math.round(container.x / GRID_SIZE);
                const gridY = Math.round(container.y / GRID_SIZE);
                
                // Validate and notify parent
                if (onObjectDragEnd) {
                  onObjectDragEnd(instance, gridX, gridY);
                }
              },
              visualEffects: {
                dragCursor: 'grabbing',
                normalCursor: 'grab',
                dragAlpha: GAME_GRID.OBJECT_DRAG_ALPHA,
                normalAlpha: 1,
              }
            });
          } else {
            // In Token Layer mode, objects are NOT interactive but stay fully visible
            objectContainer.eventMode = 'none';
          }
        } else if (isPaintMode) {
          objectContainer.alpha = GAME_GRID.OBJECT_PAINT_MODE_ALPHA;
        }
        
        objectsContainer.addChild(objectContainer);
      } catch (e) {
        console.warn('[drawGrid] Failed to add object sprite', e);
      }
    }
  }

  const structureDoorTexture = textures['structure-door'];
  const doorSpritesContainer = new PIXI.Container();

  // Draw walls on grid lines above terrain and objects.
  const wallsGraphics = new PIXI.Graphics();
  for (const [edgeKey, enabled] of Object.entries(gridWalls || {})) {
    if (!enabled) continue;
    const [axis, payload] = edgeKey.split(':');
    if (!axis || !payload) continue;
    const [aRaw, bRaw] = payload.split('-');
    const a = Number(aRaw);
    const b = Number(bRaw);
    if (Number.isNaN(a) || Number.isNaN(b)) continue;

    const wallType = enabled === true ? 'wall' : enabled;

    if (axis === 'v') {
      const x = a * GRID_SIZE;
      const y = b * GRID_SIZE;
      if (wallType === 'door' && structureDoorTexture) {
        const sprite = new PIXI.Sprite(structureDoorTexture);
        sprite.anchor.set(0.5);
        sprite.x = x;
        sprite.y = y + GRID_SIZE / 2;
        sprite.angle = 90;
        sprite.width = GRID_SIZE;
        sprite.height = GRID_SIZE * GAME_GRID.DOOR_HEIGHT_GRID_FRACTION;
        doorSpritesContainer.addChild(sprite);
      } else {
        wallsGraphics.moveTo(x, y);
        wallsGraphics.lineTo(x, y + GRID_SIZE);
      }
    } else if (axis === 'h') {
      const x = a * GRID_SIZE;
      const y = b * GRID_SIZE;
      if (wallType === 'door' && structureDoorTexture) {
        const sprite = new PIXI.Sprite(structureDoorTexture);
        sprite.anchor.set(0.5);
        sprite.x = x + GRID_SIZE / 2;
        sprite.y = y;
        sprite.width = GRID_SIZE;
        sprite.height = GRID_SIZE * GAME_GRID.DOOR_HEIGHT_GRID_FRACTION;
        doorSpritesContainer.addChild(sprite);
      } else {
        wallsGraphics.moveTo(x, y);
        wallsGraphics.lineTo(x + GRID_SIZE, y);
      }
    }
  }
  wallsGraphics.stroke({
    width: GAME_GRID.WALL_STROKE_WIDTH,
    color: GAME_GRID.WALL_STROKE_COLOR,
    alpha: GAME_GRID.WALL_STROKE_ALPHA,
  });
  viewport.addChild(wallsGraphics);
  viewport.addChild(doorSpritesContainer);

  // Draw grid lines
  const gridLinesGraphics = new PIXI.Graphics();
  viewport.addChild(gridLinesGraphics);

  for (let i = 0; i <= gridWidth; i++) {
    gridLinesGraphics.moveTo(i * GRID_SIZE, 0);
    gridLinesGraphics.lineTo(i * GRID_SIZE, gridHeight * GRID_SIZE);
  }

  for (let j = 0; j <= gridHeight; j++) {
    gridLinesGraphics.moveTo(0, j * GRID_SIZE);
    gridLinesGraphics.lineTo(gridWidth * GRID_SIZE, j * GRID_SIZE);
  }
  
  gridLinesGraphics.stroke({
    width: GAME_GRID.GRID_LINES_STROKE_WIDTH,
    color: GAME_GRID.GRID_LINES_COLOR,
    alpha: GAME_GRID.GRID_LINES_ALPHA,
  });
}

// Setup paint interaction layer - uses a single hit area and calculates cell from position
function setupPaintLayer(container, gridWidth, gridHeight, isPaintMode, paintMode, selectedPaintColor, areaPaintStart, areaPaintEnd, wallHoverCorner, wallDragStartCorner, wallDragEndCorner, onGridMouseDown, onGridMouseEnter, onGridMouseUp, isPaintingRef, viewportRef, zoom, onDeselectAll) {
  // Clear any existing children first
  container.removeChildren();
  
  if (!isPaintMode) {
    container.eventMode = 'none';
    container.interactiveChildren = false;
    container.visible = false;
    return;
  }

  container.eventMode = 'static';
  container.interactiveChildren = true;
  container.visible = true;
  const totalWidth = gridWidth * GRID_SIZE;
  const totalHeight = gridHeight * GRID_SIZE;

  // Single large hit area covering the entire grid
  const hitArea = new PIXI.Graphics();
  hitArea.rect(0, 0, totalWidth, totalHeight);
  hitArea.fill({ color: GAME_GRID.COLOR_WHITE, alpha: GAME_GRID.HIT_AREA_ALPHA });

  hitArea.eventMode = 'static';
  const isEraseTool = typeof selectedPaintColor === 'object' && (
    selectedPaintColor?.clearColor ||
    selectedPaintColor?.clearFog
  );
  hitArea.cursor = isEraseTool ? GRID_ERASE_CURSOR : 'crosshair';
  const isWallPaint = typeof selectedPaintColor === 'object' && (
    selectedPaintColor?.structure === 'wall' || selectedPaintColor?.structure === 'door'
  );
  const isWallErase = typeof selectedPaintColor === 'object' && selectedPaintColor?.clearColor && selectedPaintColor?.eraseType === 'walls';
  const isWallCornerMode = isWallPaint || isWallErase;

  // Helper to convert screen position to grid cell
  const getCellFromPosition = (globalX, globalY) => {
    if (!viewportRef.current) return null;
    
    // Convert screen coords to world coords using viewport
    const worldPos = viewportRef.current.toWorld(globalX, globalY);
    const worldX = worldPos.x;
    const worldY = worldPos.y;
    
    // Calculate grid cell
    if (worldY < 0 || worldY >= totalHeight) {
      return null;
    }
    if (worldX < 0 || worldX >= totalWidth) {
      return null;
    }
    
    const i = Math.floor(worldX / GRID_SIZE);
    const j = Math.floor(worldY / GRID_SIZE);
    
    if (i >= 0 && i < gridWidth && j >= 0 && j < gridHeight) {
      return { i, j };
    }
    return null;
  };

  let lastCell = null;
  const getClosestCorner = (globalX, globalY) => {
    if (!viewportRef.current) return null;
    const worldPos = viewportRef.current.toWorld(globalX, globalY);
    const cx = Math.round(worldPos.x / GRID_SIZE);
    const cy = Math.round(worldPos.y / GRID_SIZE);
    if (cx < 0 || cy < 0 || cx > gridWidth || cy > gridHeight) return null;
    return { x: cx, y: cy };
  };

  hitArea.on('pointerdown', (event) => {
    if (event.button === GAME_GRID.POINTER_BUTTON_PRIMARY) {
      const cell = getCellFromPosition(event.global.x, event.global.y);
      if (cell) {
        isPaintingRef.current = true;
        lastCell = cell;
        onGridMouseDown?.(cell.i, cell.j, { corner: getClosestCorner(event.global.x, event.global.y) });
      }
    }
    // Allow right-click events to propagate for viewport panning
    if (event.button === GAME_GRID.POINTER_BUTTON_SECONDARY) {
      // Don't stop propagation for right-click
      return;
    }
  });

  hitArea.on('pointermove', (event) => {
    const cell = getCellFromPosition(event.global.x, event.global.y);
    const corner = getClosestCorner(event.global.x, event.global.y);

    if (isWallCornerMode) {
      if (cell) {
        onGridMouseEnter?.(cell.i, cell.j, { corner });
      }
      return;
    }

    if (isPaintingRef.current && cell && (!lastCell || cell.i !== lastCell.i || cell.j !== lastCell.j)) {
      lastCell = cell;
      onGridMouseEnter?.(cell.i, cell.j, { corner });
    }
  });

  hitArea.on('pointerup', (event) => {
    if (isPaintingRef.current) {
      isPaintingRef.current = false;
      lastCell = null;
      onGridMouseUp?.({ corner: getClosestCorner(event.global.x, event.global.y) });
    }
  });

  hitArea.on('pointerupoutside', (event) => {
    if (isPaintingRef.current) {
      isPaintingRef.current = false;
      lastCell = null;
      onGridMouseUp?.({ corner: getClosestCorner(event.global.x, event.global.y) });
    }
  });

  if (isWallCornerMode) {
    const wallPreview = new PIXI.Graphics();
    if (wallDragStartCorner && wallDragEndCorner) {
      wallPreview.moveTo(wallDragStartCorner.x * GRID_SIZE, wallDragStartCorner.y * GRID_SIZE);
      wallPreview.lineTo(wallDragEndCorner.x * GRID_SIZE, wallDragEndCorner.y * GRID_SIZE);
      wallPreview.stroke({
        width: GAME_GRID.WALL_PREVIEW_STROKE_WIDTH,
        color: isWallErase ? GAME_GRID.COLOR_WALL_ERASE : GAME_GRID.COLOR_ACCENT_GOLD,
        alpha: GAME_GRID.WALL_PREVIEW_STROKE_ALPHA,
      });
    }

    const drawCornerCircle = (corner, fillColor) => {
      if (!corner) return;
      wallPreview.circle(
        corner.x * GRID_SIZE,
        corner.y * GRID_SIZE,
        GAME_GRID.WALL_CORNER_INNER_RADIUS
      );
      wallPreview.fill({ color: fillColor, alpha: GAME_GRID.WALL_PREVIEW_STROKE_ALPHA });
      wallPreview.circle(
        corner.x * GRID_SIZE,
        corner.y * GRID_SIZE,
        GAME_GRID.WALL_CORNER_OUTER_RADIUS
      );
      wallPreview.stroke({
        width: GAME_GRID.WALL_CORNER_STROKE_WIDTH,
        color: GAME_GRID.COLOR_BLACK,
        alpha: GAME_GRID.WALL_CORNER_STROKE_ALPHA,
      });
    };

    drawCornerCircle(
      wallHoverCorner,
      isWallErase ? GAME_GRID.COLOR_WALL_ERASE : GAME_GRID.COLOR_ACCENT_GOLD
    );
    drawCornerCircle(
      wallDragStartCorner,
      isWallErase ? GAME_GRID.COLOR_WALL_ERASE_LIGHT : GAME_GRID.COLOR_WALL_CORNER_GOLD_LIGHT
    );
    drawCornerCircle(
      wallDragEndCorner,
      isWallErase ? GAME_GRID.COLOR_WALL_ERASE : GAME_GRID.COLOR_ACCENT_GOLD
    );
    container.addChild(wallPreview);
  }

  container.addChild(hitArea);
}

function colorToHex(color) {
  if (!color) return GAME_GRID.COLOR_DEFAULT_SILVER;
  if (typeof color === 'number') return color;
  if (typeof color === 'string' && color.startsWith('#')) {
    return parseInt(color.slice(1), 16);
  }
  return GAME_GRID.COLOR_DEFAULT_SILVER;
}

function darkenColor(color, amount) {
  const r = Math.max(0, ((color >> 16) & 0xff) * (1 - amount));
  const g = Math.max(0, ((color >> 8) & 0xff) * (1 - amount));
  const b = Math.max(0, (color & 0xff) * (1 - amount));
  return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
}

export default GameGrid;
